import { countRange, monthsBetween } from "./model";
import { ReportingIcon } from "./ReportingIcons";
import { useObservatoryCopy } from "./copy";
import type { ObservatoryView } from "./schema";

type Point = { month: string; lower: number; upper: number };
function Sparkline({ rows, label }: { rows: Point[]; label: string }) {
  const { locale } = useObservatoryCopy();
  const monthLabel = (month: string) =>
    new Intl.DateTimeFormat(locale, {
      month: "short",
      year: "numeric",
      calendar: "gregory",
      timeZone: "UTC",
    }).format(new Date(`${month}-01T12:00:00Z`));
  const sorted = [...rows].sort((a, b) => a.month.localeCompare(b.month));
  const axis = monthsBetween(sorted[0]!.month, sorted.at(-1)!.month);
  const maximum = Math.max(1, ...sorted.map((r) => r.upper));
  const points = axis.map((month, i) => {
    const value = sorted.find((r) => r.month === month);
    return value
      ? {
          x: axis.length > 1 ? 6 + (i * 188) / (axis.length - 1) : 100,
          y: 40 - ((value.lower + 4.5) / maximum) * 32,
        }
      : null;
  });
  const segments: string[][] = [[]];
  for (const point of points) {
    if (point) segments.at(-1)!.push(`${point.x},${point.y}`);
    else if (segments.at(-1)!.length) segments.push([]);
  }
  return (
    <div className="observatory-hover-trend">
      <svg viewBox="0 0 200 46" role="img" aria-label={label}>
        {segments
          .filter((s) => s.length > 1)
          .map((s, i) => (
            <polyline key={i} points={s.join(" ")} />
          ))}
        {points
          .filter((p) => p !== null)
          .map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="2.5" />
          ))}
      </svg>
      <div>
        <bdi>{monthLabel(sorted[0]!.month)}</bdi>
        <bdi>{monthLabel(sorted.at(-1)!.month)}</bdi>
      </div>
    </div>
  );
}

export function MapHoverCard({
  id,
  name,
  view,
  x,
  y,
  width,
}: {
  id: string;
  name: string;
  view?: ObservatoryView | undefined;
  x: number;
  y: number;
  width: number;
}) {
  const { t } = useObservatoryCopy();
  const threats = [...(view?.dimensions.attacks ?? [])]
    .sort((a, b) => b.lower - a.lower)
    .slice(0, 2);
  const hasThreatTrends = threats.some((threat) =>
    view?.series.some((r) => r.threat === threat.label),
  );
  const months = (view?.dimensions.months ?? []).map((r) => ({
    ...r,
    month: r.label,
  }));
  return (
    <div
      id={id}
      role="tooltip"
      className="observatory-map-tooltip"
      style={{ left: x, top: y, width }}
    >
      <header>
        <strong>{name}</strong>
        <bdi>{view ? countRange(view.cases) : "—"}</bdi>
      </header>
      {!view ? (
        <span>{t("unpublishedAreas")}</span>
      ) : (
        <>
          {threats.length > 0 && (
            <ul>
              {threats.map((row) => {
                const series = view.series.filter(
                  (r) => r.threat === row.label,
                );
                return (
                  <li key={row.label}>
                    <div className="observatory-hover-threat">
                      <ReportingIcon label={row.label} />
                      <span>{row.label}</span>
                      <bdi>{countRange(row)}</bdi>
                    </div>
                    {series.length > 0 && (
                      <Sparkline
                        rows={series}
                        label={`${row.label} · ${t("timeline")}`}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {!hasThreatTrends &&
            (months.length ? (
              <>
                <span className="observatory-hover-caption">
                  {t("timeline")}
                </span>
                <Sparkline rows={months} label={t("timeline")} />
              </>
            ) : (
              <span className="observatory-hover-empty">
                {t("noMonthlyTrend")}
              </span>
            ))}
        </>
      )}
    </div>
  );
}
